<script setup lang="ts">
import { ref, computed } from 'vue'
import { ChevronDown, FolderOpen, Settings, LogOut } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useAuth } from '@/composables/useAuth'
import { useClickOutside } from '@/composables/useClickOutside'

const authStore = useAuthStore()
const { logout } = useAuth()
const isOpen = ref(false)
const menuRef = ref<HTMLElement | null>(null)

useClickOutside(menuRef, () => { isOpen.value = false })

const userAvatar = computed(() =>
  authStore.user?.avatarUrl || authStore.user?.avatar_url || '/images/logos/logo-no-text-favicon.png',
)

const userName = computed(() => authStore.user?.name || 'User')
const userEmail = computed(() => authStore.user?.email || '')

const userPlan = computed(() =>
  authStore.user?.plan || authStore.user?.subscription?.plan || 'free',
)

function hasPlan(plan: string): boolean {
  return !!plan && plan !== 'free'
}

async function handleLogout() {
  isOpen.value = false
  await logout()
}
</script>

<template>
  <div ref="menuRef" class="relative">
    <button
      class="flex items-center gap-2 py-1.5 pl-1.5 pr-3 bg-transparent border border-border rounded-full cursor-pointer transition-all duration-200 hover:bg-surface-tertiary hover:border-border-hover"
      @click.stop="isOpen = !isOpen"
    >
      <div class="relative inline-block">
        <img :src="userAvatar" :alt="userName" class="w-8 h-8 rounded-full object-cover">
        <span
          v-if="hasPlan(userPlan)"
          :class="[
            'absolute -bottom-1 -right-2 text-[8px] font-bold px-1 py-0.5 rounded text-white uppercase tracking-wide whitespace-nowrap border border-surface',
            userPlan === 'master'
              ? 'bg-gradient-to-br from-amber-500 to-orange-500'
              : 'bg-gradient-to-br from-brand to-accent-purple',
          ]"
        >
          {{ userPlan.toUpperCase().slice(0, 3) }}
        </span>
      </div>
      <ChevronDown
        :size="14"
        :class="['text-foreground-secondary transition-transform duration-200', isOpen && 'rotate-180']"
      />
    </button>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2.5"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2.5"
    >
      <div
        v-if="isOpen"
        class="absolute top-[calc(100%+8px)] right-0 w-[280px] max-md:w-[260px] bg-surface border border-border rounded-xl shadow-lg z-[1000]"
      >
        <!-- User info header -->
        <div class="flex items-center gap-3 p-4 border-b border-border">
          <img :src="userAvatar" :alt="userName" class="w-12 h-12 rounded-full object-cover">
          <div class="flex flex-col gap-0.5 overflow-hidden">
            <span class="font-semibold text-foreground whitespace-nowrap overflow-hidden text-ellipsis">{{ userName }}</span>
            <span class="text-xs text-foreground-secondary whitespace-nowrap overflow-hidden text-ellipsis">{{ userEmail }}</span>
          </div>
        </div>

        <!-- Connected accounts -->
        <div v-if="authStore.user?.providers?.length" class="p-3">
          <div class="text-[11px] font-semibold uppercase text-foreground-muted mb-2 px-2">Connected Accounts</div>
          <div
            v-for="provider in authStore.user.providers"
            :key="provider.id"
            class="flex items-center gap-2.5 p-2 rounded-md mb-1 last:mb-0"
          >
            <svg v-if="provider.provider === 'github'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="shrink-0">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            <svg v-else-if="provider.provider === 'gitlab'" width="16" height="16" viewBox="0 0 24 24" fill="#fc6d26" class="shrink-0">
              <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.45.044 13.587a.924.924 0 00.331 1.023L12 23.054l11.625-8.443a.92.92 0 00.33-1.024"/>
            </svg>
            <span class="flex-1 text-[13px] text-foreground">@{{ provider.username || provider.provider_username }}</span>
            <span class="w-2 h-2 rounded-full bg-success" />
          </div>
        </div>

        <div class="h-px bg-border mx-0 my-1" />

        <RouterLink
          to="/projects"
          class="flex items-center gap-3 px-4 py-3 text-foreground text-sm no-underline cursor-pointer transition-colors duration-150 hover:bg-surface-tertiary hover:no-underline"
          @click="isOpen = false"
        >
          <FolderOpen :size="16" class="text-foreground-secondary shrink-0" />
          <span>Projects</span>
        </RouterLink>
        <RouterLink
          to="/settings"
          class="flex items-center gap-3 px-4 py-3 text-foreground text-sm no-underline cursor-pointer transition-colors duration-150 hover:bg-surface-tertiary hover:no-underline"
          @click="isOpen = false"
        >
          <Settings :size="16" class="text-foreground-secondary shrink-0" />
          <span>Settings</span>
        </RouterLink>

        <div class="h-px bg-border mx-0 my-1" />

        <button
          class="flex items-center gap-3 px-4 py-3 w-full text-left text-sm text-error bg-transparent border-none cursor-pointer transition-colors duration-150 hover:bg-surface-tertiary"
          @click="handleLogout"
        >
          <LogOut :size="16" class="text-error shrink-0" />
          <span>Logout</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
